import React, { useEffect, useRef } from 'react';
import PropTypes, { InferProps } from 'prop-types';

declare global {
  interface Window {
    initMap: () => void;
    google: unknown;
  }
}

interface MarkerData {
  latitude?: number;
  longitude?: number;
  marker: google.maps.Marker | null;
  infoWindow: google.maps.InfoWindow | null;
  prefixNumber?: string;
  name?: string;
  milesFromSearchPoint?: number;
  distributorType?: string;
  dynamicData?: {
    Address1: string;
    Address2: string;
    FullAddress?: string;
    City: string;
    DistributorTypeDisplayName: string;
    State: string;
    PostalCode: string;
    Phone: string;
    Website: string;
    ProfileUrl?: string;
  };
  opened?: boolean;
}

function DistributorMap({
  apiKey,
  data,
  zoomRadius,
  websiteLabel,
  profileLabel,
  searchThisAreaLabel,
  imagesPath,
  isMetalContractor,
  hideMiles
}: InferProps<typeof DistributorMap.propTypes>) {
  const centerLatitude = data?.latitude || 0;
  const centerLongitude = data?.longitude || 0;
  const zoom = zoomRadius || 50;
  const mapCenter = { lat: centerLatitude, lng: centerLongitude };
  let markers: MarkerData[] = [];
  const markersRef = useRef<MarkerData[]>([]);

  // Custom custom event for zoom change
  const zoomChangeEvent = new CustomEvent('mapZoomChanged', {
    detail: {
      zoom: zoom
    }
  });

  const handleZoomChanged = async (map: google.maps.Map) => {
    zoomChangeEvent.detail.zoom = map.getZoom() as number;
    window.dispatchEvent(zoomChangeEvent);
  };

  const createCustomControl = async (map: google.maps.Map) => {
    const customControlDiv = document.createElement('div');
    customControlDiv.className = 'distributor-map__custom-search-area-button';
    customControlDiv.style.display = 'none';

    const customControlDivContent =
      `${searchThisAreaLabel}` +
      `<svg viewBox="0, 0, 13, 16" role="presentation">` +
      `<use xlink:href="#icon-search-area"></use>` +
      `</svg>`;
    customControlDiv.innerHTML = customControlDivContent;
    map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(
      customControlDiv
    );

    setTimeout(() => {
      customControlDiv.style.display = 'none';
    });

    // Event listener for click event on custom control button
    customControlDiv.addEventListener('click', () => {
      customControlDiv.style.display = 'none';
      const newCenter = map.getCenter();
      const customEvent = new CustomEvent('searchThisArea', {
        detail: {
          latitude: newCenter?.lat(),
          longitude: newCenter?.lng()
        },
        bubbles: true
      });
      window.dispatchEvent(customEvent);
    });

    map.addListener('dragend', () => {
      customControlDiv.style.display = 'inline-flex';
    });

    setTimeout(() => {
      map.addListener('zoom_changed', () => {
        customControlDiv.style.display = 'inline-flex';
      });
    }, 1000);
  };

  const createInfoWindowContent = (markerData: MarkerData) => {
    if (markerData.milesFromSearchPoint)
      markerData.milesFromSearchPoint =
        Math.round(markerData.milesFromSearchPoint * 10) / 10;
    if (
      markerData.dynamicData?.Phone &&
      !(markerData.dynamicData.Phone[0] === '(')
    ) {
      markerData.dynamicData.Phone =
        markerData.dynamicData.Phone.length === 10
          ? `(${markerData.dynamicData.Phone.substring(
              0,
              3
            )}) ${markerData.dynamicData.Phone.substring(
              3,
              6
            )}-${markerData.dynamicData.Phone.substring(6, 10)}`
          : `${markerData.dynamicData.Phone.substring(
              0,
              1
            )} (${markerData.dynamicData.Phone.substring(
              1,
              4
            )}) ${markerData.dynamicData.Phone.substring(
              4,
              7
            )}-${markerData.dynamicData.Phone.substring(7, 11)}`;
    }

    const websiteText = markerData.dynamicData?.Website
      ? `<a href="${markerData.dynamicData?.Website}" class="distributor-card__actions-website" 
      data-layer='[{"event_attributes": {"zip_code": ${markerData.dynamicData.PostalCode}},"event": "result_click","event_category": "result click","event_action": "distributor locator","event_label": "website click"}]'
      >` +
        `${websiteLabel}` +
        `</a>`
      : '';

    const _imagesPath = imagesPath || '/Areas/GAF/images';

    if (isMetalContractor) {
      return `<div class="distributor-card__container contractor-finder__marker">
          <div class="distributor-card__top-container">
            <div class="distributor-card__content-wrapper">
              <div class="distributor-card__heading">
                <h2 class="distributor-card__title">${markerData.name}</h2>
              </div>
              <div class="distributor-card__address-wrapper">
                ${
                  !hideMiles
                    ? `<p class="distributor-card__get-directions">
                  <span class="distributor-card__get-directions-label">${markerData.milesFromSearchPoint} mi</span>
                </p>`
                    : ''
                }
                <p class="distributor-card__address">${
                  markerData.dynamicData?.FullAddress
                }</p>
              </div>
            </div>
          </div>
          <div class="distributor-card__actions distributor-card__actions--metal">
          <a href="tel:${
            markerData.dynamicData?.Phone
          }" class="distributor-card__actions-phone link--icon-left" data-layer='[{"event_attributes": {"zip_code": ${
        markerData.dynamicData?.PostalCode
      }},"event": "result_click","event_category": "result click","event_action": "distributor locator","event_label": "phone click"}]'>${
        markerData.dynamicData?.Phone
      }<svg class="red-phone-icon" viewBox="0 0 13 16" role="presentation"><use xlink:href="#red-phone-icon"></use></svg></a>
          <a href="${
            markerData.dynamicData?.ProfileUrl
          }" class="link link--arrow distributor-card__actions-profile">${profileLabel}</a>
          </div>
        </div>
        `;
    }

    return (
      `<div class="distributor-card__container">` +
      `<div class="distributor-card__top-container">` +
      `<div class="distributor-card__content-wrapper">` +
      `<div class="distributor-card__heading">` +
      `<h3 class="distributor-card__title">${markerData.name}</h3>` +
      `<p class="distributor-card__description">${markerData.dynamicData?.DistributorTypeDisplayName}</p>` +
      `</div>` +
      `<div class="distributor-card__address-wrapper">` +
      `<p class="distributor-card__address">` +
      `${markerData.dynamicData?.Address1}` +
      `${
        markerData.dynamicData?.Address2
          ? `, ${markerData.dynamicData?.Address2}`
          : ''
      }` +
      `${
        markerData.dynamicData?.City ? `, ${markerData.dynamicData?.City}` : ''
      }` +
      `${
        markerData.dynamicData?.State
          ? `, ${markerData.dynamicData?.State}`
          : ''
      }` +
      `</p>` +
      `<p class="distributor-card__get-directions">` +
      `<a href="https://www.google.com/maps/dir/?api=1&destination=${markerData.dynamicData?.Address1}" class="link distributor-card__get-directions-link" target="_blank" data-layer='[{"event_attributes": {"zip_code": ${markerData.dynamicData?.PostalCode}},"event": "result_click","event_category": "result click","event_action": "distributor locator","event_label": "website click"}]'>` +
      `<span>${
        markerData.milesFromSearchPoint
          ? `${markerData.milesFromSearchPoint} mi`
          : ''
      } - Get Directions</span>` +
      `<svg viewBox="0, 0, 8, 14" role="presentation">` +
      `<use xlink:href="#chevron-right"></use>` +
      `</svg>` +
      `</a>` +
      `</p>` +
      `</div>` +
      `</div>` +
      `<div class="distributor-card__logo-wrapper">` +
      `<img src="${_imagesPath}${markerData.distributorType
        ?.toLowerCase()
        .replace(' ', '-')}.png" alt="${
        markerData.distributorType
      }" class="distributor-card__logo">` +
      `</div>` +
      `</div>` +
      `<div class="distributor-card__actions">` +
      `<a href="tel:${markerData.dynamicData?.Phone}" class="distributor-card__actions-phone link--icon-left" data-layer='[{"event_attributes": {"zip_code": ${markerData.dynamicData?.PostalCode}},"event": "result_click","event_category": "result click","event_action": "distributor locator","event_label": "phone click"}]'>` +
      `${markerData.dynamicData?.Phone}` +
      `<svg class="red-phone-icon" viewBox="0, 0, 13, 16" role="presentation">` +
      `<use xlink:href="#red-phone-icon"></use>` +
      `</svg>` +
      `</a>` +
      websiteText +
      `</a>` +
      `</div>` +
      `</div>`
    );
  };

  useEffect(() => {
    window.initMap = () => {
      const mapOptions = {
        center: mapCenter,
        zoom: zoom,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: {
          position: google.maps.ControlPosition.RIGHT_TOP
        }
      };

      const mapElement = document.getElementById('map') as HTMLElement;
      const map = new window.google.maps.Map(mapElement, mapOptions);

      const markerSize = isMetalContractor ? 45 : 25;
      const markerHoverSize = isMetalContractor ? 65 : 40;

      // Set the map markers
      const markerIcon = {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`<svg width="35" height="35" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect x="0.707111" width="23.7486" height="23.7486" transform="matrix(0.707111 -0.707103 0.707111 0.707103 0.207105 18)" fill="#2A2A2A"/>
<rect x="0.707111" width="23.7486" height="23.7486" transform="matrix(0.707111 -0.707103 0.707111 0.707103 0.207105 18)" stroke="#DCDCDC"/>
</svg>`),
        scaledSize: new google.maps.Size(markerSize, markerSize)
      };
      const hoveredMarkerIcon = {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`<svg width="51" height="51" viewBox="0 0 51 51" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect x="1.91421" y="25.5" width="33.3553" height="33.3553" transform="rotate(-45 1.91421 25.5)" fill="white"/>
<rect x="1.91421" y="25.5" width="33.3553" height="33.3553" transform="rotate(-45 1.91421 25.5)" stroke="#D71920" stroke-width="2"/>
<rect width="24.7486" height="24.7486" transform="matrix(0.707111 -0.707102 0.707111 0.707102 8 25.5)" fill="#D71920"/>
</svg>`),
        scaledSize: new google.maps.Size(markerHoverSize, markerHoverSize)
      };

      if (data && data.distributors) {
        markers = data.distributors.map(distributor => ({
          latitude: distributor?.latitude as number,
          longitude: distributor?.longitude as number,
          marker: null,
          infoWindow: null,
          prefixNumber: distributor?.prefixNumber as string,
          name: distributor?.name as string,
          milesFromSearchPoint: distributor?.milesFromSearchPoint as number,
          distributorType: distributor?.distributorType as string,
          dynamicData: {
            Address1: distributor?.dynamicData?.Address1 as string,
            Address2: distributor?.dynamicData?.Address2 as string,
            FullAddress: distributor?.dynamicData?.FullAddress as string,
            City: distributor?.dynamicData?.City as string,
            DistributorTypeDisplayName: distributor?.dynamicData
              ?.DistributorTypeDisplayName as string,
            State: distributor?.dynamicData?.State as string,
            PostalCode: distributor?.dynamicData?.PostalCode as string,
            Phone: distributor?.dynamicData?.Phone as string,
            Website: distributor?.dynamicData?.Website as string,
            ProfileUrl: distributor?.dynamicData?.ProfileUrl as string
          }
        }));

        markersRef.current = markers;
        markers.forEach(markerData => {
          const marker = new google.maps.Marker({
            position: {
              lat: markerData.latitude as number,
              lng: markerData.longitude as number
            },
            map: map,
            icon: markerIcon,
            ...(isMetalContractor
              ? {
                  label: {
                    color: '#fff',
                    fontFamily: 'ITC Avant Garde Gothic Std Demi',
                    fontSize: '14px',
                    fontWeight: 'normal',
                    text: markerData.prefixNumber?.toString() || ''
                  }
                }
              : {})
          });

          // Create the info window
          const infoWindow = new google.maps.InfoWindow({
            content: createInfoWindowContent(markerData),
            minWidth: 300
          });

          // Assign the infoWindow to the markerData
          markerData.marker = marker;
          markerData.infoWindow = infoWindow;

          // Add event listeners for marker events
          marker.addListener('mouseover', () => {
            marker.setIcon(hoveredMarkerIcon);
          });

          google.maps.event.addListener(marker, 'click', function () {
            marker.setIcon(markerIcon);
          });

          marker.addListener('click', () => {
            // Close all other info windows
            markers.forEach(markerData => {
              markerData.infoWindow?.close();
              markerData.marker?.setIcon(markerIcon);
              markerData.opened = false;
            });

            markerData.marker?.setIcon(hoveredMarkerIcon);
            infoWindow.open(map, marker);

            // Set the default icon to the other markers
            markers.forEach(markerData => {
              if (markerData.marker !== marker) {
                markerData.marker?.setIcon(markerIcon);
              }
            });

            markerData.opened = true;
          });

          marker.addListener('mouseout', () => {
            if (markerData.opened !== true) {
              marker.setIcon(markerIcon);
            }
          });
        });

        // Set the map bounds
        if (data.distributors.length > 0) {
          const bounds = new google.maps.LatLngBounds();
          markers.forEach(marker => {
            bounds.extend(
              new google.maps.LatLng(
                marker.latitude as number,
                marker.longitude as number
              )
            );
          });
          map.fitBounds(bounds);
        }

        // Add event listener to close the info window and reset markers when the map is clicked
        google.maps.event.addListener(map, 'click', () => {
          markers.forEach(markerData => {
            markerData.infoWindow?.close();
            markerData.marker?.setIcon(markerIcon);
          });
        });

        // Add event listeners for the custom event
        const handleCardHover = (event: CustomEvent) => {
          // close all info windows
          markersRef.current.forEach(markerData => {
            markerData.infoWindow?.close();
            markerData.marker?.setIcon(markerIcon);
          });

          const { latitude, longitude } = event.detail;
          markersRef.current.forEach(markerData => {
            if (
              markerData.latitude === latitude &&
              markerData.longitude === longitude
            ) {
              markerData.marker?.setIcon(hoveredMarkerIcon);
            } else {
              markerData.marker?.setIcon(markerIcon);
            }
          });
        };

        //Create the custom control button to Search this area
        createCustomControl(map);

        if (data.distributors.length <= 0) {
          //center the map on the default location
          map.setCenter(mapCenter);
          map.setZoom(15);
        }

        // Event listener for zoom_changed event (user zooms in or out the map)
        setTimeout(() => {
          map.addListener('zoom_changed', () => {
            handleZoomChanged(map as google.maps.Map);
          });
        }, 1000);

        window.addEventListener(
          'myCustomEvent',
          handleCardHover as EventListener
        );
        return () => {
          window.removeEventListener(
            'myCustomEvent',
            handleCardHover as EventListener
          );
        };
      } else {
        map.setCenter(mapCenter);
        map.setZoom(15);
      }
      return;
    };

    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initMap`;
      script.defer = true;
      document.head.appendChild(script);

      // Clean up the script tag when the component unmounts
      return () => {
        document.head.removeChild(script);
      };
    } else {
      window.initMap();
      return undefined;
    }
  }, [data]);

  return <div id="map" className="distributor-locator__map"></div>;
}

DistributorMap.propTypes = {
  apiKey: PropTypes.string.isRequired,
  data: PropTypes.shape({
    page: PropTypes.number,
    pageSize: PropTypes.number,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    searchRadius: PropTypes.number,
    totalResultCount: PropTypes.number,
    distributors: PropTypes.arrayOf(
      PropTypes.shape({
        prefixNumber: PropTypes.string,
        name: PropTypes.string,
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        milesFromSearchPoint: PropTypes.number,
        customerId: PropTypes.string,
        distributorId: PropTypes.string,
        distributorType: PropTypes.string,
        dynamicData: PropTypes.shape({
          Address1: PropTypes.string,
          Address2: PropTypes.string,
          FullAddress: PropTypes.string,
          City: PropTypes.string,
          DistributorTypeDisplayName: PropTypes.string,
          Fax: PropTypes.string,
          Phone: PropTypes.string,
          PostalCode: PropTypes.string,
          ProductTypes: PropTypes.array,
          State: PropTypes.string,
          StoreNumber: PropTypes.string,
          Website: PropTypes.string,
          ProfileUrl: PropTypes.string
        })
      })
    )
  }),
  zoomRadius: PropTypes.number,
  websiteLabel: PropTypes.string,
  profileLabel: PropTypes.string,
  searchThisAreaLabel: PropTypes.string.isRequired,
  imagesPath: PropTypes.string,
  isMetalContractor: PropTypes.bool,
  hideMiles: PropTypes.bool
};

export default DistributorMap;
